import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { auth } from "../lib/auth";
import { getPremiumPromotionDeadline, type PremiumPromotion } from "../lib/promotion";
import styles from "./PremiumPromotionLanding.module.css";

interface PremiumPromotionLandingProps {
  promotion: PremiumPromotion;
  onContinue: () => void;
  onExit: () => void;
  onExpire?: () => void;
  presentation?: "page" | "modal";
}

const featureIcons = ["∞", "⊘", "☷", "♫", "☆"];

function formatCountdown(deadline: number, now: number) {
  const totalSeconds = Math.max(0, Math.ceil((deadline - now) / 1000) - 1);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const format = (value: number) => String(value).padStart(2, "0");

  return {
    hours: format(hours),
    minutes: format(minutes),
    seconds: format(seconds),
    expired: totalSeconds <= 0,
  };
}

export function PremiumPromotionLanding({
  promotion,
  onContinue,
  onExit,
  onExpire,
  presentation = "page",
}: PremiumPromotionLandingProps) {
  const { t } = useTranslation();
  const user = auth.user();
  const deadline = useMemo(
    () => getPremiumPromotionDeadline(promotion, user),
    [promotion.campaignId, promotion.durationHours, user?.id]
  );
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const countdown = formatCountdown(deadline, now);

  useEffect(() => {
    if (countdown.expired) onExpire?.();
  }, [countdown.expired, onExpire]);

  const discount = Math.max(
    0,
    Math.round(((promotion.originalPrice - promotion.price) / promotion.originalPrice) * 100)
  );
  const features = [
    t("subscription.promotionUnlimited", "Unlimited Listening"),
    t("subscription.promotionNoAds", "No Ads"),
    t("subscription.promotionQueue", "Full Playlist & Queue"),
    t("subscription.promotionQuality", "High Quality Sound"),
    t("subscription.promotionExclusive", "Exclusive Features & Early Access"),
  ];

  return (
    <section
      className={`${styles.page} ${presentation === "modal" ? styles.modalPage : ""}`}
      aria-labelledby="premium-promotion-title"
    >
      <div className={styles.artwork} aria-hidden="true" />
      <div className={styles.scrim} aria-hidden="true" />

      <header className={styles.topBar}>
        <div className={styles.brand}>
          <div>
            <strong>MUSIC</strong>
            <span>Powered by Yours</span>
          </div>
          <img src="/logo.png" alt="" />
        </div>
        <button type="button" className={styles.skip} onClick={onExit}>
          {t("subscription.promotionSkip", "Skip")}
        </button>
      </header>

      <div className={styles.content}>
        <div className={styles.offerIntro}>
          <span className={styles.offerBadge}>{t("subscription.promotionLimited", "Limited Time Offer")}</span>
          <p className={styles.eyebrow}>{t("subscription.promotionEyebrow", "Yours Music Premium")}</p>
          <h1 id="premium-promotion-title">
            {t("subscription.promotionTitle", "More than music. Made for you.")}
          </h1>
          <p className={styles.subtitle}>
            {t("subscription.promotionSubtitle", "Listen without limits and unlock the full Yours Music experience.")}
          </p>
        </div>

        <div className={styles.timer} aria-label={t("subscription.promotionCountdown", "Offer countdown")}> 
          {(["hours", "minutes", "seconds"] as const).map((unit, index) => (
            <React.Fragment key={unit}>
              {index > 0 && <span className={styles.timerSeparator}>:</span>}
              <div className={styles.timeGroup}>
                <strong>{countdown[unit]}</strong>
                <span>{t(`subscription.promotion${unit[0].toUpperCase()}${unit.slice(1)}`, unit)}</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        <div className={styles.offerCard}>
          <div className={styles.priceRow}>
            <div>
              <strong>{promotion.price.toLocaleString()} <small>MMK</small></strong>
              <span>{t("subscription.promotionNormalPrice", "Normal price")} <s>{promotion.originalPrice.toLocaleString()} MMK</s></span>
            </div>
            <span className={styles.savings}>{t("subscription.promotionSave", "Save")}<strong>{discount}%</strong></span>
          </div>

          <ul className={styles.features}>
            {features.map((feature, index) => (
              <li key={feature}>
                <span aria-hidden="true">{featureIcons[index]}</span>
                <b>{feature}</b>
              </li>
            ))}
          </ul>

          <button
            type="button"
            className={styles.cta}
            onClick={onContinue}
            disabled={countdown.expired}
          >
            {countdown.expired
              ? t("subscription.promotionExpired", "Offer ended")
              : t("subscription.promotionCta", {
                  price: promotion.price.toLocaleString(),
                  defaultValue: "Get Premium — {{price}} Ks",
                })}
          </button>
        </div>

        <footer className={styles.footer}>
          <span>{t("subscription.promotionJoin", "Join thousands of music lovers on Yours Music")}</span>
          <i><b /> <b /> <b /></i>
        </footer>
      </div>
    </section>
  );
}

export default PremiumPromotionLanding;
